/* istanbul ignore file  */

import { AxiosError } from 'axios';
import { ROUTES } from 'constants/routes';
import { IS_TEST_MODE } from 'consts';
import { QueryCache, QueryClient } from 'react-query';
import {
	IAppConfig,
	IUnauthorizedResponse,
	UnauthorizedReason
} from 'types/api';
import { RecursivePartial } from 'types/typeutils';
import { env } from 'app/Env/env';
import { IServerFeatureFlags } from 'types/featureflags';
import { HTTP_STATUS_CODE } from './api';
import QueryKeys from './queryKeys';

export const generateQueryClient = (options?: {
	appConfig?: RecursivePartial<IAppConfig> | null;
	serverFeatureFlags?: IServerFeatureFlags | null;
}) => {
	const qc = new QueryClient({
		defaultOptions: {
			queries: {
				retry: IS_TEST_MODE
					? false
					: (count, error) => {
							const axiosError = error as AxiosError;
							if (
								axiosError?.response?.status === HTTP_STATUS_CODE.unauthorized
							) {
								return false;
							}
							// only retry get requests and up to 3 retries.
							return (
								axiosError.config?.method?.toLowerCase() === 'get' && count < 3
							);
						},
				staleTime: !IS_TEST_MODE ? 1000 * 4 : Infinity // 4 seconds
			}
		},
		queryCache: new QueryCache({
			onError: (error: any) => {
				if (error?.response?.status === HTTP_STATUS_CODE.unauthorized) {
					const errorData = error?.response?.data as
						IUnauthorizedResponse | undefined;
					const reason = encodeURIComponent(
						errorData?.unauthorizedReason || UnauthorizedReason.NO_AUTH
					);
					const base = (env.baseRoute || '/').replace(/\/$/, '');
					if (window.location.pathname !== `${base}${ROUTES.unauthorized}`) {
						window.location.assign(
							`${base}${ROUTES.unauthorized}?reason=${reason}`
						);
					}
				}
			}
		})
	});

	if (options?.appConfig) {
		qc.setQueryData([QueryKeys.appConfig], options?.appConfig);
	}

	if (options?.serverFeatureFlags) {
		qc.setQueryData([QueryKeys.featureFlags], options?.serverFeatureFlags);
	}

	return qc;
};
